import { Component, OnInit, OnDestroy, AfterViewInit, inject } from '@angular/core';
import { FeatureFlagsService } from './feature-flags.service';
import { combineLatest, filter, Observable, Subject, takeUntil, map, share } from 'rxjs';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { OpenReplayService } from './open-replay.service';
import { ClarityService } from './clarity.service';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../environments/environment';
import { MetaDataService } from './metadata.service';
import { MainNavigationService } from './main-navigation.service';
import { MainMenu } from './main-menu-item.interface';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { TopNavigationComponent } from './top-navigation/top-navigation.component';
import { SiteFooterComponent } from './site-footer/site-footer.component';
import { MaintenanceModeComponent } from './maintenance-mode/maintenance-mode.component';
import { ComingSoonComponent } from './coming-soon/coming-soon.component';

@Component({
    selector: 'tea-shop-fe-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [TopNavigationComponent, RouterOutlet, SiteFooterComponent, MaintenanceModeComponent, ComingSoonComponent, NgTemplateOutlet, AsyncPipe]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private featureFlagsService = inject(FeatureFlagsService);
  private router = inject(Router);
  private OpenReplayService = inject(OpenReplayService);
  private clarityService = inject(ClarityService);
  private logger = inject(NGXLogger);
  private metadataService = inject(MetaDataService);
  private mainNavigationService = inject(MainNavigationService);

  title = 'tea-shop-fe';

  showStore$!: Observable<boolean>;
  isLoading = true;
  maintenanceMode$!: Observable<boolean>;
  comingSoon$!: Observable<boolean>;
  securing$!: Observable<boolean>;
  mainMenu$!: Observable<MainMenu>;
  private destroy$ = new Subject<void>();
  private isNavigating = false;

  ngAfterViewInit() {
    this.logger.log('AppComponent:ngAfterViewInit');
    if (environment.OPTIMISATION_OPEN_REPLAY) {
      this.OpenReplayService.start();
    } else {
      this.logger.warn('OpenReplay disabled');
    }
    if (environment.OPTIMISATION_CLARITY) {
      this.clarityService.start();
    } else {
      this.logger.warn('Clarity disabled');
    }
  }

  ngOnInit() {
    this.logger.log('AppComponent');
    
    // Load main menu data
    this.mainMenu$ = this.mainNavigationService.getMainMenu().pipe(share());
    
    this.maintenanceMode$ = this.featureFlagsService.getBooleanValue$('maintenance-mode', false);
    this.comingSoon$ = this.featureFlagsService.getBooleanValue$('coming-soon', false);

    this.showStore$ = combineLatest([
      this.maintenanceMode$,
      this.comingSoon$
    ]).pipe(
      map(([maintenance, coming]) => !maintenance && !coming)
    );

    this.securing$ = combineLatest([
      this.maintenanceMode$,
      this.comingSoon$,
      this.showStore$
    ]).pipe(
      map(([maintenance, coming, store]) => !maintenance && !coming && !store)
    );

    // Subscribe to both navigation events and feature flag changes
    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );

    navigationEnd$.subscribe((event) => {
      const canonicalURL = `https://sencha-tea.com${event.urlAfterRedirects}`;
      this.metadataService.updateMetadata({url: canonicalURL});
      this.metadataService.setCanonicalLink(canonicalURL);
      this.logger.debug('Canonical URL set to:', canonicalURL);
    });


    // Combine latest values from feature flags and navigation
    combineLatest([
      navigationEnd$,
      this.featureFlagsService.getBooleanValue$('maintenance-mode', false),
      this.featureFlagsService.getBooleanValue$('coming-soon', false)
    ])
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe(([, maintenanceMode, comingSoon]) => {
      if (!this.isNavigating) {
        this.isNavigating = true;
        this.handleNavigation(maintenanceMode, comingSoon);
        setTimeout(() => {
          this.isNavigating = false;
        }, 100);
      }
    });

    // Doesn't use the logger service, this is meant to be for the console
    console.log(
      '%c🔒 Security Researcher? We have a VDP! 🔒\n' +
      '%cVisit /vulnerability-disclosure-program',
      'color: #166534; font-size: 20px; font-weight: bold;',
      'color: #166534; font-size: 16px;'
    );
  }

  private handleNavigation(maintenanceMode: boolean, comingSoon: boolean) {
    const currentUrl = this.router.url;
    if (maintenanceMode && !currentUrl.includes('/maintenance')) {
      this.router.navigate(['/maintenance'], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    } else if (comingSoon && !currentUrl.includes('/coming-soon')) {
      this.router.navigate(['/coming-soon'], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    } else if (!maintenanceMode && !comingSoon && 
              (currentUrl.includes('/maintenance') || currentUrl.includes('/coming-soon'))) {
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl') || '/';
      this.router.navigateByUrl(returnUrl);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
